import { useState, useEffect, useCallback } from 'react';
import { 
  Users, Search, Plus, Pencil, Eye, Phone, Mail, MapPin, 
  Building2, FileText, ShoppingBag, IndianRupee, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { customerAPI } from '@/services/api';
import { formatDate, formatCurrency } from '@/utils/dateFormat';

// Indian states list
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal'
];

const initialFormData = {
  name: '',
  mobile: '',
  email: '',
  address: '',
  district: '',
  city: '',
  state: '',
  pincode: '',
  gstin: '',
  pan: ''
};

const StoreCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  
  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  
  // Purchase history
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const storeId = user?.entity_id;

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const response = await customerAPI.listByStore(storeId, { search });
      setCustomers(response.data.customers || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch customers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [storeId, search, toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  // Open create dialog
  const handleOpenCreate = () => {
    setSelectedCustomer(null);
    setFormData(initialFormData);
    setFormDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name || '',
      mobile: customer.mobile || '',
      email: customer.email || '',
      address: customer.address || '',
      district: customer.district || '',
      city: customer.city || '',
      state: customer.state || '',
      pincode: customer.pincode || '',
      gstin: customer.gstin || '',
      pan: customer.pan || ''
    });
    setFormDialogOpen(true);
  };

  // Open view dialog with purchase history
  const handleOpenView = async (customer) => {
    setSelectedCustomer(customer);
    setViewDialogOpen(true);
    setLoadingHistory(true);
    
    try {
      const response = await customerAPI.getPurchaseHistory(customer.id);
      setPurchaseHistory(response.data.purchase_history || []);
    } catch (error) {
      console.error('Failed to fetch purchase history:', error);
      setPurchaseHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Save customer
  const handleSave = async () => {
    if (!formData.name || !formData.mobile) {
      toast({ title: 'Validation Error', description: 'Name and Mobile are required', variant: 'destructive' });
      return;
    }

    if (formData.mobile.length < 10) {
      toast({ title: 'Validation Error', description: 'Mobile number must be at least 10 digits', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (selectedCustomer) {
        // Update
        await customerAPI.update(selectedCustomer.id, formData);
        toast({ title: 'Success', description: 'Customer updated successfully' });
      } else {
        // Create
        await customerAPI.create({ ...formData, store_id: storeId });
        toast({ title: 'Success', description: 'Customer created successfully' });
      }
      setFormDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save customer', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-customers-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-500" />
            Customer Management
          </h1>
          <p className="text-gray-500 mt-1">Manage your store customers</p>
        </div>
        <Button 
          onClick={handleOpenCreate}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Search & Stats */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or mobile..."
                className="pl-10 bg-gray-50 border-gray-300"
                data-testid="search-customer-input"
              />
            </div>
            <Badge variant="outline" className="px-4 py-2 text-sm">
              Total: {total} customers
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-4 border-b border-gray-100">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-500" />
            Customer List
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Customers Found</p>
              <p className="text-sm mt-1">Add your first customer to get started</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-600">Customer</TableHead>
                    <TableHead className="text-gray-600">Contact</TableHead>
                    <TableHead className="text-gray-600">Location</TableHead>
                    <TableHead className="text-gray-600">GST/PAN</TableHead>
                    <TableHead className="text-gray-600 text-center">Purchases</TableHead>
                    <TableHead className="text-gray-600 text-right">Total Spent</TableHead>
                    <TableHead className="text-gray-600 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer.id} className="border-gray-200 hover:bg-gray-50">
                      <TableCell>
                        <p className="font-medium text-gray-800">{customer.name}</p>
                        <p className="text-xs text-gray-500">
                          Since {formatDate(customer.created_at, false)}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-gray-600">
                          <Phone className="w-3 h-3" />
                          <span className="text-sm">{customer.mobile}</span>
                        </div>
                        {customer.email && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                            <Mail className="w-3 h-3" />
                            <span>{customer.email}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {customer.state || customer.city || customer.district ? (
                          <div className="text-sm text-gray-600">
                            {[customer.city || customer.district, customer.state].filter(Boolean).join(', ')}
                            {customer.pincode && <span className="text-gray-400"> - {customer.pincode}</span>}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {customer.gstin ? (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            GST: {customer.gstin}
                          </Badge>
                        ) : customer.pan ? (
                          <Badge variant="outline" className="text-xs">
                            PAN: {customer.pan}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium text-emerald-600">{customer.total_purchases || 0}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium text-gray-800">
                          {formatCurrency(customer.total_spent || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenView(customer)}
                            className="text-gray-500 hover:text-gray-700"
                            data-testid={`view-customer-${customer.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(customer)}
                            className="text-gray-500 hover:text-gray-700"
                            data-testid={`edit-customer-${customer.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Customer Dialog */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Required Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Customer Name"
                  className="bg-gray-50 border-gray-300"
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Mobile *</Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => setFormData(p => ({ ...p, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  placeholder="10-digit mobile"
                  className="bg-gray-50 border-gray-300"
                  disabled={!!selectedCustomer}
                  data-testid="customer-mobile-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Email</Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="email@example.com"
                className="bg-gray-50 border-gray-300"
              />
            </div>

            {/* Address Section */}
            <div className="pt-2 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-3">Address Details</p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-gray-700">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                    placeholder="Street address, building, etc."
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">District</Label>
                    <Input
                      value={formData.district}
                      onChange={(e) => setFormData(p => ({ ...p, district: e.target.value }))}
                      placeholder="District"
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                      placeholder="City"
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">State</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(val) => setFormData(p => ({ ...p, state: val }))}
                    >
                      <SelectTrigger className="bg-gray-50 border-gray-300">
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Postal Code</Label>
                    <Input
                      value={formData.pincode}
                      onChange={(e) => setFormData(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      placeholder="6-digit PIN"
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Details */}
            <div className="pt-2 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-3">Tax Details (Optional)</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">GSTIN</Label>
                  <Input
                    value={formData.gstin}
                    onChange={(e) => setFormData(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                    placeholder="22AAAAA0000A1Z5"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">PAN</Label>
                  <Input
                    value={formData.pan}
                    onChange={(e) => setFormData(p => ({ ...p, pan: e.target.value.toUpperCase() }))}
                    placeholder="AAAAA0000A"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="save-customer-btn"
            >
              {saving ? 'Saving...' : 'Save Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Customer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              Customer Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{selectedCustomer.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {selectedCustomer.mobile}
                      </span>
                      {selectedCustomer.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {selectedCustomer.email}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setViewDialogOpen(false);
                      handleOpenEdit(selectedCustomer);
                    }}
                    className="border-gray-300"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                </div>
                
                {/* Address */}
                {(selectedCustomer.address || selectedCustomer.state) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {[selectedCustomer.address, selectedCustomer.district, selectedCustomer.city, selectedCustomer.state, selectedCustomer.pincode].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
                
                {/* Tax Details */}
                {(selectedCustomer.gstin || selectedCustomer.pan) && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex gap-4">
                    {selectedCustomer.gstin && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <Building2 className="w-3 h-3 mr-1" />
                        GSTIN: {selectedCustomer.gstin}
                      </Badge>
                    )}
                    {selectedCustomer.pan && (
                      <Badge variant="outline">
                        <FileText className="w-3 h-3 mr-1" />
                        PAN: {selectedCustomer.pan}
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-50 p-4 rounded-lg text-center">
                  <ShoppingBag className="w-6 h-6 mx-auto text-emerald-500 mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">{selectedCustomer.total_purchases || 0}</p>
                  <p className="text-xs text-emerald-600">Total Purchases</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <IndianRupee className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(selectedCustomer.total_spent || 0)}</p>
                  <p className="text-xs text-blue-600">Total Spent</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <Calendar className="w-6 h-6 mx-auto text-amber-500 mb-2" />
                  <p className="text-sm font-medium text-amber-700">
                    {selectedCustomer.last_purchase_date ? formatDate(selectedCustomer.last_purchase_date, false) : 'Never'}
                  </p>
                  <p className="text-xs text-amber-600">Last Purchase</p>
                </div>
              </div>

              {/* Purchase History */}
              <div className="border border-gray-200 rounded-lg">
                <div className="p-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-medium text-gray-800">Recent Purchases</h4>
                </div>
                <div className="p-4">
                  {loadingHistory ? (
                    <p className="text-center text-gray-500 py-4">Loading...</p>
                  ) : purchaseHistory.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No purchase history found</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {purchaseHistory.map((sale, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-emerald-600">{sale.bill_number}</p>
                            <p className="text-xs text-gray-500">{formatDate(sale.bill_datetime)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-800">{formatCurrency(sale.grand_total)}</p>
                            <p className="text-xs text-gray-500">{sale.items?.length || 0} items</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-gray-300">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreCustomersPage;
